import { useState, useEffect } from 'react';
import { Card, Row, Col, Button, ProgressBar } from 'react-bootstrap';
import { useApi } from '../../../contexts/ApiProvider';

export default function TripLedger({ tripId, onBudgetInitialized }) {
    const api = useApi();
    const [budgetData, setBudgetData] = useState(null);

    useEffect(() => { fetchBudget(); }, [tripId]);

    const fetchBudget = async () => {
        const res = await api.get(`/travel/trips/${tripId}/budget`);
        if (res.ok) setBudgetData(res.body);
    };

    const handleInitializeBudget = async () => {
        const res = await api.post(`/travel/trips/${tripId}/budget`);
        if (res.ok) {
            onBudgetInitialized(); // Tell parent to refresh the badge
            fetchBudget(); // Refresh this component
        } else {
            alert(res.body?.message || "Failed to initialize budget.");
        }
    };

    if (!budgetData) return null;

    return (
        <div className="bg-white p-4 fade-in">
            {!budgetData.linked ? (
                <div className="text-center py-5 border rounded-4 bg-light">
                    <i className="bi bi-safe2 display-4 d-block mb-3 text-muted opacity-25"></i>
                    <h5 className="fw-bold">No Budget Envelope Linked</h5>
                    <p className="text-muted small mb-4">Click below to instantly generate a budget tracking envelope.</p>
                    <Button variant="primary" className="rounded-pill fw-bold shadow-sm px-4" onClick={handleInitializeBudget}>
                        <i className="bi bi-link-45deg me-2 fs-5 align-middle"></i>Initialize Budget Envelope
                    </Button>
                </div>
            ) : (
                <>
                    <Row className="mb-4 g-3">
                        <Col md={4}>
                            <Card className="border-0 bg-light shadow-sm">
                                <Card.Body className="text-center py-3">
                                    <h6 className="text-muted small fw-bold mb-1 uppercase">Total Budget</h6>
                                    <h4 className="fw-bold text-primary mb-0">${budgetData.total_budget.toFixed(2)}</h4>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card className="border-0 bg-light shadow-sm">
                                <Card.Body className="text-center py-3">
                                    <h6 className="text-muted small fw-bold mb-1 uppercase">Total Spent</h6>
                                    <h4 className="fw-bold text-danger mb-0">${budgetData.total_spent.toFixed(2)}</h4>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card className="border-0 bg-light shadow-sm">
                                <Card.Body className="text-center py-3">
                                    <h6 className="text-muted small fw-bold mb-1 uppercase">Remaining</h6>
                                    <h4 className={`fw-bold mb-0 ${budgetData.remaining >= 0 ? 'text-success' : 'text-danger'}`}>
                                        ${budgetData.remaining.toFixed(2)}
                                    </h4>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    <div className="mb-5 px-2">
                        <ProgressBar
                            variant={budgetData.total_spent > budgetData.total_budget ? 'danger' : (budgetData.total_spent > budgetData.total_budget * 0.8 ? 'warning' : 'success')}
                            now={budgetData.total_budget > 0 ? (budgetData.total_spent / budgetData.total_budget) * 100 : 0}
                            style={{ height: '12px' }}
                        />
                    </div>

                    {budgetData.breakdown && budgetData.breakdown.length > 0 && (
                        <div className="mb-5">
                            <h6 className="fw-bold text-muted mb-3 uppercase letter-spacing-1 small">Spend by Category</h6>
                            <div className="d-flex flex-wrap gap-2">
                                {budgetData.breakdown.map(b => (
                                    <div key={b.tag} className="border rounded-pill px-3 py-2 bg-light d-flex align-items-center gap-2 shadow-sm">
                                        <span className="fw-bold text-primary">{b.tag}</span>
                                        <span className="text-muted small fw-bold border-start ps-2 border-secondary border-opacity-25">${b.amount.toFixed(2)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <h5 className="fw-bold mb-3 border-bottom pb-2">Trip Expenses</h5>
                    {budgetData.transactions.length === 0 ? (
                        <p className="text-muted text-center py-4">No transactions categorized for this trip yet.</p>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle border-light">
                                <thead className="bg-light text-muted small fw-bold uppercase letter-spacing-1">
                                    <tr>
                                        <th className="rounded-start ps-3 border-0">Date</th><th className="border-0">Vendor</th>
                                        <th className="border-0">Notes</th><th className="text-end rounded-end pe-3 border-0">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {budgetData.transactions.map(txn => (
                                        <tr key={txn.id}>
                                            <td className="ps-3 text-muted small">{txn.date ? new Date(txn.date).toLocaleDateString() : '---'}</td>
                                            <td className="fw-bold">{txn.vendor}</td><td className="text-muted small">{txn.notes || '--'}</td>
                                            <td className="text-end pe-3 fw-bold text-danger">-${txn.amount.toFixed(2)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
